import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Grid,
  Paper,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  HelpOutline,
  ContactSupport,
  QuestionAnswer,
  Email,
  Phone,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { Link } from 'react-router-dom';

const FAQ = () => {
  const { t } = useTranslation();
  const { mode } = useTheme();
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const categories = [
    {
      id: 'general',
      name: t('faq.categories.general'),
      icon: <HelpOutline />,
      color: '#3b82f6',
    },
    {
      id: 'products',
      name: t('faq.categories.products'),
      icon: <QuestionAnswer />,
      color: '#8b5cf6',
    },
    {
      id: 'technical',
      name: t('faq.categories.technical'),
      icon: <ContactSupport />,
      color: '#f59e0b',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pt: { xs: 12, md: 16 },
        pb: 8,
        background: mode === 'dark'
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f8fafc 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: mode === 'dark'
            ? `radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
               radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)`
            : `radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
               radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 8,
            mt: { xs: 4, sm: 6, md: 4 },
            opacity: 0,
            animation: 'fadeInUp 1s ease-out forwards',
            '@keyframes fadeInUp': {
              '0%': { opacity: 0, transform: 'translateY(30px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
              px: 3,
              py: 1.5,
              borderRadius: 8,
              background: mode === 'dark'
                ? 'rgba(59, 130, 246, 0.15)'
                : 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
            }}
          >
            <HelpOutline sx={{ fontSize: 32, color: '#3b82f6' }} />
            <Typography
              variant="overline"
              sx={{
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 800,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: '#3b82f6',
              }}
            >
              {t('faq.title')}
            </Typography>
          </Box>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
              fontWeight: 900,
              mb: 2,
              background: mode === 'dark'
                ? 'linear-gradient(135deg, #fff 0%, #94a3b8 100%)'
                : 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
            }}
          >
            {t('faq.headline')}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', md: '1.25rem' },
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              maxWidth: '700px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            {t('faq.subtitle')}
          </Typography>
        </Box>

        {/* Categories */}
        <Grid
          container
          spacing={3}
          sx={{
            mb: 6,
            opacity: 0,
            animation: 'fadeIn 1s ease-out 0.2s forwards',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 },
            },
          }}
        >
          {categories.map((category) => (
            <Grid item xs={12} sm={4} key={category.id}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 4,
                  background: mode === 'dark'
                    ? 'rgba(30, 41, 59, 0.9)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: mode === 'dark'
                    ? '1px solid rgba(148, 163, 184, 0.2)'
                    : '1px solid rgba(148, 163, 184, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 10px 30px ${category.color}40`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: `${category.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {React.cloneElement(category.icon, {
                    sx: { fontSize: 28, color: category.color },
                  })}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: mode === 'dark' ? '#fff' : '#0f172a',
                  }}
                >
                  {category.name}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* FAQ Items */}
        {categories.map((category, categoryIndex) => (
          <Box
            key={category.id}
            sx={{
              mb: 6,
              opacity: 0,
              animation: 'fadeInUp 1s ease-out forwards',
              animationDelay: `${0.3 + categoryIndex * 0.1}s`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 3,
              }}
            >
              <Chip
                icon={React.cloneElement(category.icon, {
                  sx: { fontSize: 20, color: category.color },
                })}
                label={category.name}
                sx={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  px: 2,
                  py: 3,
                  background: `${category.color}20`,
                  color: category.color,
                  border: `1px solid ${category.color}40`,
                }}
              />
            </Box>

            {t(`faq.${category.id}`, { returnObjects: true }).map((item, index) => (
              <Accordion
                key={`${category.id}-${index}`}
                expanded={expanded === `${category.id}-${index}`}
                onChange={handleChange(`${category.id}-${index}`)}
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: mode === 'dark'
                    ? 'rgba(30, 41, 59, 0.9)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: mode === 'dark'
                    ? '1px solid rgba(148, 163, 184, 0.2)'
                    : '1px solid rgba(148, 163, 184, 0.3)',
                  boxShadow: 'none',
                  transition: 'all 0.3s ease',
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    boxShadow: `0 8px 25px ${category.color}30`,
                    borderColor: category.color,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMore
                      sx={{
                        color: expanded === `${category.id}-${index}`
                          ? category.color
                          : mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : 'rgba(0, 0, 0, 0.6)',
                      }}
                    />
                  }
                  sx={{
                    px: 3,
                    py: 2,
                    '&:hover': {
                      background: mode === 'dark'
                        ? 'rgba(59, 130, 246, 0.05)'
                        : 'rgba(59, 130, 246, 0.03)',
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: '1rem', md: '1.125rem' },
                      fontWeight: 600,
                      color: expanded === `${category.id}-${index}`
                        ? category.color
                        : mode === 'dark'
                        ? '#fff'
                        : '#0f172a',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    px: 3,
                    py: 3,
                    borderTop: mode === 'dark'
                      ? '1px solid rgba(148, 163, 184, 0.2)'
                      : '1px solid rgba(148, 163, 184, 0.3)',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: '0.9375rem', md: '1rem' },
                      color: mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.8)'
                        : 'rgba(0, 0, 0, 0.7)',
                      lineHeight: 1.7,
                    }}
                  >
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))}

        {/* Contact CTA */}
        <Paper
          sx={{
            p: { xs: 4, md: 6 },
            mt: 8,
            borderRadius: 4,
            textAlign: 'center',
            background: mode === 'dark'
              ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)'
              : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
            border: mode === 'dark'
              ? '1px solid rgba(59, 130, 246, 0.3)'
              : '1px solid rgba(59, 130, 246, 0.2)',
            opacity: 0,
            animation: 'fadeIn 1s ease-out 0.8s forwards',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.5rem', md: '2rem' },
              fontWeight: 700,
              mb: 2,
              color: mode === 'dark' ? '#fff' : '#0f172a',
            }}
          >
            {t('faq.stillHaveQuestions')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', md: '1.125rem' },
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              mb: 4,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            {t('faq.contactDescription')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              startIcon={<Email />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 3,
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  boxShadow: '0 12px 30px rgba(59, 130, 246, 0.4)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {t('faq.contactUs')}
            </Button>
            <Button
              component="a"
              href="tel:+982188423760"
              variant="outlined"
              size="large"
              startIcon={<Phone />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 3,
                borderColor: '#3b82f6',
                color: '#3b82f6',
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  borderColor: '#8b5cf6',
                  background: mode === 'dark'
                    ? 'rgba(59, 130, 246, 0.1)'
                    : 'rgba(59, 130, 246, 0.05)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {t('faq.callUs')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default FAQ;

